import { useState, type ChangeEvent, type FormEvent } from 'react';
import { Icon } from '@iconify/react';
import { api, type ContactPayload } from '../../lib/api';
import './Module.scss';

type FormErrors = Partial<Record<keyof ContactPayload, string>>;
type SubmitStatus = { type: 'success' | 'error' | null; message: string };

const initialForm: ContactPayload = {
  name: '', email: '', phone: '', company: '', service: '',
  budgetRange: '', preferredContact: '', subject: '', message: '',
  consent: false, source: 'site', pageUrl: '',
};

export function FormContact() {
  const [form, setForm] = useState<ContactPayload>(initialForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [status, setStatus] = useState<SubmitStatus>({ type: null, message: '' });
  const [loading, setLoading] = useState(false);

  const change = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = event.target;
    const next = type === 'checkbox' ? (event.target as HTMLInputElement).checked : value;
    setForm((current) => ({ ...current, [name]: next }));
    setErrors((current) => ({ ...current, [name]: undefined }));
  };

  const validate = (): boolean => {
    const next: FormErrors = {};
    if (!form.name.trim()) next.name = 'Informe seu nome.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) next.email = 'Informe um e-mail válido.';
    if (!form.subject.trim()) next.subject = 'Informe o assunto.';
    if (form.message.trim().length < 10) next.message = 'Explique sua necessidade com pelo menos 10 caracteres.';
    if (!form.consent) next.consent = 'É necessário autorizar o uso dos dados para responder ao contato.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setStatus({ type: null, message: '' });
    if (!validate()) return;

    setLoading(true);
    try {
      await api.contact({
        ...form,
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        pageUrl: window.location.href,
      });
      setStatus({ type: 'success', message: 'Mensagem enviada. Em breve entraremos em contato.' });
      setForm(initialForm);
      setErrors({});
    } catch (error) {
      setStatus({
        type: 'error',
        message: error instanceof Error ? error.message : 'Não foi possível enviar agora.',
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <form className="contact-form" onSubmit={submit} noValidate>
      {status.type && (
        <div className={`alert-box ${status.type === 'success' ? 'alert-success' : 'alert-danger'}`} role="alert">
          <Icon icon={status.type === 'success' ? 'lucide:check-circle-2' : 'lucide:alert-circle'} aria-hidden="true" />
          <span>{status.message}</span>
        </div>
      )}

      <div className="form-row">
        <Field id="name" label="Nome completo *" value={form.name} error={errors.name} onChange={change} disabled={loading} />
        <Field id="email" label="E-mail *" type="email" value={form.email} error={errors.email} onChange={change} disabled={loading} />
      </div>
      <div className="form-row">
        <Field id="phone" label="Telefone / WhatsApp" type="tel" value={form.phone ?? ''} onChange={change} disabled={loading} />
        <Field id="company" label="Empresa" value={form.company ?? ''} onChange={change} disabled={loading} />
      </div>
      <div className="form-row">
        <label className="form-group">
          <span>Serviço de interesse</span>
          <select name="service" value={form.service ?? ''} onChange={change} disabled={loading}>
            <option value="">Selecione</option>
            <option>Desenvolvimento Web</option>
            <option>Redes e Provedores</option>
            <option>Servidores e Cloud</option>
            <option>DNS e Domínios</option>
            <option>Segurança</option>
            <option>Outro</option>
          </select>
        </label>
        <label className="form-group">
          <span>Faixa de investimento</span>
          <select name="budgetRange" value={form.budgetRange ?? ''} onChange={change} disabled={loading}>
            <option value="">Prefiro não informar</option>
            <option>Até R$ 1.000</option>
            <option>R$ 1.000 a R$ 5.000</option>
            <option>R$ 5.000 a R$ 10.000</option>
            <option>Acima de R$ 10.000</option>
            <option>Projeto recorrente</option>
          </select>
        </label>
      </div>
      <div className="form-row">
        <label className="form-group">
          <span>Prefere retorno por</span>
          <select name="preferredContact" value={form.preferredContact ?? ''} onChange={change} disabled={loading}>
            <option value="">Qualquer canal</option>
            <option value="email">E-mail</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="phone">Telefone</option>
          </select>
        </label>
        <Field id="subject" label="Assunto *" value={form.subject} error={errors.subject} onChange={change} disabled={loading} />
      </div>
      <label className="form-group">
        <span>Mensagem do projeto *</span>
        <textarea id="message" name="message" rows={6} maxLength={5000} value={form.message} onChange={change} disabled={loading} aria-invalid={Boolean(errors.message)} aria-describedby={errors.message ? 'message-error' : undefined} />
        {errors.message && <span id="message-error" className="field-error-text">{errors.message}</span>}
      </label>
      <label className="form-consent">
        <input type="checkbox" name="consent" checked={form.consent} onChange={change} disabled={loading} aria-invalid={Boolean(errors.consent)} />
        <span>Autorizo o tratamento dos dados enviados exclusivamente para retorno deste contato, conforme a política de privacidade.</span>
      </label>
      {errors.consent && <span className="field-error-text">{errors.consent}</span>}
      <button type="submit" className="btn btn-primary" disabled={loading}>
        {loading ? 'Enviando…' : 'Enviar mensagem'}
      </button>
    </form>
  );
}

interface FieldProps {
  id: keyof ContactPayload;
  label: string;
  type?: 'text' | 'email' | 'tel';
  value: string;
  error?: string;
  disabled?: boolean;
  onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
}

function Field({ id, label, type = 'text', value, error, disabled, onChange }: FieldProps) {
  const errorId = `${String(id)}-error`;
  return (
    <label className="form-group">
      <span>{label}</span>
      <input
        id={String(id)}
        name={String(id)}
        type={type}
        value={value}
        onChange={onChange}
        disabled={disabled}
        maxLength={180}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? errorId : undefined}
      />
      {error && <span id={errorId} className="field-error-text">{error}</span>}
    </label>
  );
}
